import React, { useState } from 'react';
import { 
  PhoneOutlined, 
  MailOutlined, 
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  ExportOutlined,
  MessageOutlined,
  CheckSquareOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {
  ContactCard,
  ContactHeader,
  ContactAvatar,
  ContactInfo,
  StatusBadge,
  ContactDetails,
  CollapsibleSection,
  CollapsibleHeader,
  CollapsibleContent,
  EmptyState
} from './styled';
import QuickSmsComposer from './QuickSmsComposer';
import QuickTaskForm from './QuickTaskForm';

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Format phone for display
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const ContactPreviewCard = ({ contact, isLoading, phoneNumber }) => {
  const [smsOpen, setSmsOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  
  // Show empty state if no contact and not loading
  if (!contact && !isLoading) {
    const digits = phoneNumber?.replace(/\D/g, '') || '';
    
    return (
      <EmptyState>
        <FontAwesomeIcon icon={faAddressCard} />
        <div className="title">No contact found</div>
        <div className="subtitle">
          {digits.length < 7 
            ? 'Enter a phone number to find matching contacts'
            : 'No contact matches this number'}
        </div>
      </EmptyState>
    );
  }
  
  // Show loading state
  if (isLoading) {
    return (
      <EmptyState>
        <LoadingOutlined style={{ fontSize: 32, marginBottom: 12 }} />
        <div className="title">Searching...</div>
        <div className="subtitle">Looking for matching contacts</div>
      </EmptyState>
    );
  }
  
  // Render contact card
  return (
    <div>
      <ContactCard>
        <ContactHeader>
          <ContactAvatar>
            {getInitials(contact.name)}
          </ContactAvatar>
          <ContactInfo>
            <div className="name">
              {contact.name}
              {contact.status && (
                <StatusBadge $status={contact.status}>
                  {contact.status}
                </StatusBadge>
              )}
            </div>
            {contact.id && (
              <div
                className="view-profile"
                onClick={() => window.open(`/admin/members/${contact.id}`, '_blank')}
              >
                View Full Profile <ExportOutlined />
              </div>
            )}
          </ContactInfo>
        </ContactHeader>
        
        <ContactDetails>
          {contact.phone && (
            <div className="detail-row">
              <PhoneOutlined />
              {formatPhoneDisplay(contact.phone)}
            </div>
          )}
          {contact.email && (
            <div className="detail-row">
              <MailOutlined />
              {contact.email}
            </div>
          )}
          {contact.location && (
            <div className="detail-row">
              <EnvironmentOutlined />
              {contact.location}
            </div>
          )}
          {contact.lastContact && (
            <div className="detail-row">
              <CalendarOutlined />
              Last contact: {new Date(contact.lastContact).toLocaleDateString()}
            </div>
          )}
          {contact.role && (
            <div className="detail-row">
              <UserOutlined />
              {contact.role}
            </div>
          )}
        </ContactDetails>
      </ContactCard>
      
      {/* Quick SMS Section */}
      <CollapsibleSection>
        <CollapsibleHeader
          $open={smsOpen}
          onClick={() => setSmsOpen(!smsOpen)}
        >
          <div className="section-title">
            <MessageOutlined />
            Quick SMS
          </div>
          <FontAwesomeIcon icon={faChevronDown} className="chevron" />
        </CollapsibleHeader>
        <CollapsibleContent $open={smsOpen}>
          <QuickSmsComposer 
            contact={contact} 
            phoneNumber={phoneNumber}
          />
        </CollapsibleContent>
      </CollapsibleSection>
      
      {/* Quick Task Section */}
      <CollapsibleSection>
        <CollapsibleHeader
          $open={taskOpen}
          onClick={() => setTaskOpen(!taskOpen)}
        >
          <div className="section-title">
            <CheckSquareOutlined />
            Quick Task
          </div>
          <FontAwesomeIcon icon={faChevronDown} className="chevron" />
        </CollapsibleHeader>
        <CollapsibleContent $open={taskOpen}>
          <QuickTaskForm 
            contact={contact}
          />
        </CollapsibleContent>
      </CollapsibleSection>
    </div>
  );
};

export default ContactPreviewCard;
